import { createSelector } from 'reselect';
import { List, Map } from 'immutable';

import { isExtensionKey, isQueueKey, keyIn } from 'flow/utils';
import { CallMethodType } from 'flow/utils/call-utils';
import {
    filterExtensionsByName,
    getCountryCode,
} from 'flow/utils/extension-utils';
import { isTeamsFullClient } from 'flow/utils/app-utils';
import { isNotDirectMessageSession } from '../utils/message-utils-TW';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';

export const getIsSinglePaneMode = (state) => {
    const layoutColumns = state.app.get('layoutColumns');
    return layoutColumns === 1;
};

export const getOwnExtensionKey = (state) => {
    return state.app.getIn(['extension', 'key']);
};

export const getOwnFixedNumber = (state) => {
    return state.app.getIn(['extension', 'contact', 'fixed', 'number']);
};

export const getOwnMobileNumber = (state) => {
    return state.app.getIn(['extension', 'contact', 'mobile', 'number']);
};

export const getOwnTechnicalNumber = (state) => {
    return state.app.getIn([
        'extension',
        'contact',
        'technicalNumber',
        'number',
    ]);
};

export const getOwnExtension = (state) => {
    return state.extensions.getIn(['extensions', getOwnExtensionKey(state)]);
};

export const getOwnContact = (state) => {
    return state.app.getIn(['extension', 'contact']);
};

export const getOwnSipUserInfo = (state) => {
    return state.app.getIn(['extension', 'sipInfo']);
};

export const getOwnCountryCode = (state) => {
    const loggedInKey = getOwnExtensionKey(state);
    const loggedInCustomer = state.customer.get(loggedInKey);
    const loggedInContact = getOwnContact(state);
    return getCountryCode(loggedInContact, loggedInCustomer);
};

export const getUserCountryCode = (state, extensionKey) => {
    const customer = state.customer.get(extensionKey);
    const contact = state.extensions.getIn([
        'extensions',
        extensionKey,
        'contact',
    ]);
    return getCountryCode(contact, customer);
};

export const getOwnCountryCodeOrNull = (state) => {
    const loggedInKey = getOwnExtensionKey(state);
    if (loggedInKey === undefined) {
        return null;
    }
    const loggedInCustomer = state.customer.get(loggedInKey);
    const loggedInContact = getOwnContact(state);
    return getCountryCode(loggedInContact, loggedInCustomer);
};

export const isTechnicalNumberUser = (state, extensionKey) => {
    return state.extensions.hasIn([extensionKey, 'contact', 'technicalNumber']);
};

export const getStatusOfExtension = (state, extension) => {
    return (
        extension &&
        extension.has('key') &&
        isExtensionKey(extension.get('key')) &&
        state.extensions.getIn(['statuses', extension.get('key')])
    );
};

export const getStatusFromExtensionKey = (state, extensionKey) => {
    return state.extensions.getIn(['statuses', extensionKey]);
};

export const getExtensionKeyFromSession = (session, extensionKey) => {
    if (isNotDirectMessageSession(session)) {
        return null;
    }
    return session
        .get('members', List())
        .map((member) => member.get('extensionKey'))
        .find((key) => key !== extensionKey);
};

export const getExtensionFromContactKey = (state, contactKey) => {
    return state.extensions
        .get('extensions')
        .find(
            (extension) => extension.getIn(['contact', 'key']) === contactKey,
        );
};

export const getHasPresenceNotification = (
    state,
    ownExtensionKey,
    targetEntityKey,
) => {
    return getExtensions(state).hasIn(
        [ownExtensionKey, 'presenceNotifications', targetEntityKey],
        false,
    );
};

export const getCallMethod = (state) => {
    const callMethodString = state.app.get('callMethod');
    const ownExtensionKey = getOwnExtensionKey(state);
    const loggedInCustomer = state.customer.getIn([ownExtensionKey, 'key']);

    let defaultCallMethod = IS_UAE_HOST
        ? CallMethodType.LOCAL
        : CallMethodType.ALWAYS_ASK;

    //See WEB-11146 for reference and why this is needed
    if (loggedInCustomer === 'customer-221490') {
        defaultCallMethod = CallMethodType.MOBILE;
    }

    const callMethod = CallMethodType.get(callMethodString);
    if (callMethod === CallMethodType.MOBILE && !hasMobileExtension(state)) {
        return CallMethodType.ALWAYS_ASK;
    }
    return callMethod ? callMethod : defaultCallMethod;
};

export const getAutoAnswerWithIpPhone = (state) => {
    return state.app.get('autoAnswerWithIpPhone') || false;
};

export const hasMobileExtension = (state) => {
    return state.app.hasIn(['extension', 'mobileExtension', 'number']);
};

export const ownUserHasTechnicalNumber = (state) => {
    return state.app.hasIn(['extension', 'contact', 'technicalNumber']);
};

export const hasTeamsIntegrationExtension = (state) => {
    return !!state.app.getIn(['extension', 'staticTrunkMapping']);
};

export const isPhoneEnabled = (state) => {
    return state.phone.get('enabled');
};

export const getQueueDescriptionFromQueueId = (state, queueId) => {
    const queues = state.pbx.get('queues');
    return queues && queues.getIn([queueId, 'description']);
};

export const getQueueNumberFromQueueId = (state, queueId) => {
    if (!state || !state.pbx) {
        return null;
    }

    const queues = state.pbx.get('queues');
    return queues && queues.has(queueId)
        ? queues.getIn([queueId, 'number', 'number'])
        : null;
};

export const getQueueNameForQueue = (state, queueKey) => {
    return state.pbx.getIn(['queues', queueKey, 'description']);
};

export const getActiveProfileForQueue = (state, queueKey) => {
    return state.pbx.getIn(['queues', queueKey, 'activeProfile'], null);
};

export const getCallDialogNumber = (state) => {
    return state.app.getIn(['callDialog', 'number']);
};

export const getCallDialogExtensionToCall = (state) => {
    return state.app.getIn(['callDialog', 'extensionToCall']);
};

export const getExtraCallOptions = (state) =>
    state.app.getIn(['callDialog', 'extraCallOptions']);

export const selectCallDialogType = (state) =>
    state.app.getIn(['callDialog', 'dialogType']);

export const getQueueMembers = (state, props) => {
    const entityKey = props.match.params.entityKey;
    if (isQueueKey(entityKey)) {
        return state.pbx.getIn(['queueMembers', entityKey]);
    } else {
        throw new Error('Not implemented: ' + entityKey);
    }
};

export const getQueueWaves = (state, props) => {
    const entityKey = props.match.params.entityKey;
    if (isQueueKey(entityKey)) {
        return state.pbx.getIn(['queues', entityKey, 'queueWaves']);
    } else {
        throw new Error('Not implemented: ' + entityKey);
    }
};

export const makeGetExtensionsInQueue = () => {
    return createSelector(
        [getExtensions, getQueueMembers],
        (extensions, queueMembers) => {
            if (queueMembers) {
                const memberExtensionKeys = queueMembers.map((member) =>
                    member.get('extensionKey'),
                );
                return extensions.filter((extension) =>
                    memberExtensionKeys.contains(extension.get('key')),
                );
            } else {
                return null;
            }
        },
    );
};

export const makeGetStatusesForExtensions = () => {
    return createSelector(
        [makeGetExtensionsInQueue(), getExtensionStatuses],
        (extensions, statuses) => {
            if (!extensions) {
                return null;
            }
            return statuses.filter(keyIn(...extensions.keys()));
        },
    );
};

export const getExtensions = (state) => state.extensions.get('extensions');
export const getExtensionStatuses = (state) => state.extensions.get('statuses');

export const getLoggedInFixedNumberCountryCode = (state) => {
    const { app } = state;
    return app.getIn(['extension', 'contact', 'fixed', 'countryCode']);
};

export const getIsLoggedIn = (state) => {
    return state.app.get('state') === 'ready';
};

export const getAllowedVersion = (state) => state.app.get('allowedVersion');

export const getNumberLookup = (numberLookups = Map(), number) => {
    if (numberLookups.isEmpty()) {
        return null;
    }
    return numberLookups.has(number) ? numberLookups.get(number) : null;
};

export const getOpenTopConnection = (state) => {
    const topConnection = state.app.get('topConnection');
    // Check for both opening and open websockets
    if (
        topConnection &&
        (topConnection.readyState === 0 || topConnection.readyState === 1)
    ) {
        return topConnection;
    }
    return null;
};

export const getSearchText = (state) => state.app.get('searchText');

/** Whether anything is being searched for at all - for callers that switch on
 *  searching rather than on what is searched for, so they stand still while the
 *  phrase itself changes. */
export const selectHasSearchText = (state) => !!state.app.get('searchText');

export const getExtensionsFilteredBySearchText = createSelector(
    [getSearchText, getExtensions, getOwnExtensionKey],
    (searchText, extensions, myExtensionKey) => {
        const others = extensions.delete(myExtensionKey);

        if (!searchText) {
            return others;
        }
        const filteredExtensions = filterExtensionsByName(others, searchText);
        return filteredExtensions;
    },
);

export const getChannels = (state) => state.channels.get('channels', Map());

export const getSnackbarData = (state) => state.app.get('snackbarList').first();

export const hasSnackbarsWaiting = (state) =>
    state.app.get('snackbarList').size > 1;

export const geti18next = (state) => state.app.get('i18next');

export const getAppStartTime = (state) => state.app.get('appStartTime');

export const getCallWaitingEnabled = (state) => {
    return state.phone.getIn(['callWaiting', 'callWaiting']);
};

export const selectAppLayoutColumns = (state) => state.app.get('layoutColumns');

export const getLoggedInUserCustomerKey = (state) => {
    const ownExtensionKey = getOwnExtensionKey(state);
    const loggedInCustomer = state.customer.get(ownExtensionKey);
    return loggedInCustomer && loggedInCustomer.get('key');
};

export const getLoggedInUsersRetailerKey = (state) => {
    const ownExtensionKey = getOwnExtensionKey(state);
    const loggedInCustomer = state.customer.get(ownExtensionKey);
    return loggedInCustomer && loggedInCustomer.getIn(['retailer', 'key']);
};

export const customerIsTelavox = (state) => {
    return getLoggedInUserCustomerKey(state) === 'customer-43';
};

export const customerIsCention = (state) => {
    return getLoggedInUserCustomerKey(state) === 'customer-226864';
};

export const isRetailer = (state, retailerKey) => {
    return getLoggedInUsersRetailerKey(state) === retailerKey;
};

export const getShouldUseTwoHourSoonLoggedInThreshold = (state) => {
    return getLoggedInUserCustomerKey(state) === 'customer-178798';
};

export const getQueueIsOfTypeOmni = (state, queueEntityKey) => {
    const subtype = state.pbx.getIn(['queues', queueEntityKey, 'DTOSubtype']);

    return subtype === 'OmnichannelQueueDTO';
};

export const selectNameInApp = (state) => {
    const firstName = state.app.getIn(
        ['extension', 'contact', 'firstName'],
        '',
    );
    const lastName = state.app.getIn(['extension', 'contact', 'lastName'], '');
    return firstName ? `${firstName} ${lastName}` : '';
};

export const userHasQueueWithOmniEnabled = (state) => {
    return !!state.pbx.get('queues', Map()).find((queue) => {
        return getQueueIsOfTypeOmni(state, queue.get('key'));
    });
};

export const getIsAppEmbedded = (state) => {
    return state.app.get('embedded');
};

export const selectIsTeamsFullClient = (state) => {
    return isTeamsFullClient(state.app.get('embedded'));
};
